import React, { useState } from "react";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { dateTimeMask, timeMask } from "../../../common/dateMask";
import { parseFloatToFixed } from "../../../extras";
import QRCode from "react-qr-code";
import { cashierTransactionTypeFilter } from "../../../utils/cashier";
import { formatFinance } from "../../../utils/formatters";

const ThermalTicketRow = ({ label, value, bold = false }) => {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: bold ? "bold" : "normal" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: bold ? "bold" : "normal" }}>
        {value}
      </Typography>
    </Box>
  );
};

const CashierClosingTicket = ({
  startDate,
  endDate,
  cashier,
  reportData,
  backgroundColor = "#ffffff",
}) => {
  const [expandedCancelled, setExpandedCancelled] = useState(false);

  const qrCodeUrl = `/historico/caixa?id=${cashier.id}`;

  const getChannelData = (channelName) => {
    const channelData = reportData.channels?.[channelName];

    return {
      orders: channelData?.orders || 0,
      finance: channelData?.finance || "0.00",
    };
  };

  const calculateFinalBalance = () => {
    const startValue = parseFloat(
      reportData.financial_summary?.start_value || 0
    );
    const totalSales = parseFloat(
      reportData.financial_summary?.money_finance || 0
    );
    const totalAdditions = parseFloat(cashier.total_additions / 100 || 0);
    const totalWithdrawals = parseFloat(cashier.total_withdrawals / 100 || 0);

    return startValue + totalSales + totalAdditions - totalWithdrawals;
  };

  const getTotalOrders = () => {
    return reportData.financial_summary?.total_orders || 0;
  };

  const getTotalFinance = () => {
    return reportData.financial_summary?.total_finance || "0.00";
  };

  const getCancelledOrdersTotal = () => {
    if (!reportData.channels) {
      return 0;
    }

    return Object.values(reportData.channels).reduce((total, channel) => {
      return total + (channel.orders_cancelled || 0);
    }, 0);
  };

  const getCancelledOrdersFinance = () => {
    return reportData.full_report?.totals?.cancelled_orders_finance || 0;
  };

  return (
    <Box
      sx={{
        fontFamily: '"Courier New", monospace',
        bgcolor: backgroundColor,
        color: "#000000",
      }}
    >
      <Box elevation={0} sx={{ bgcolor: "#ffffff" }}>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
            FECHAMENTO DE CAIXA
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {dateTimeMask(startDate)} - {dateTimeMask(endDate)}
          </Typography>

          <Divider sx={{ borderStyle: "dashed", borderWidth: "1px", my: 1 }} />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}
          >
            RESUMO DO CAIXA
          </Typography>

          <ThermalTicketRow
            label="Saldo Inicial:"
            value={formatFinance(
              parseFloat(reportData.financial_summary?.start_value || 0)
            )}
          />
          <ThermalTicketRow
            label="+ Vendas:"
            value={formatFinance(
              parseFloat(reportData.financial_summary?.money_finance || 0)
            )}
          />
          <ThermalTicketRow
            label="+ Entradas de Caixa:"
            value={formatFinance(
              parseFloat(cashier.total_additions / 100 || 0)
            )}
          />
          <ThermalTicketRow
            label="- Saídas de Caixa:"
            value={formatFinance(
              parseFloat(cashier.total_withdrawals / 100 || 0)
            )}
          />

          <Divider sx={{ my: 1 }} />

          <ThermalTicketRow
            label="= SALDO FINAL:"
            value={formatFinance(calculateFinalBalance())}
            bold={true}
          />
        </Box>

        <Divider sx={{ borderStyle: "dashed", borderWidth: "1px", my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}
          >
            VENDAS POR CANAL
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Canal
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Qtde
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    Balcão
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {getChannelData("slapiepos").orders}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {formatFinance(
                      parseFloat(getChannelData("slapiepos").finance)
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    iFood
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {getChannelData("ifood").orders}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {formatFinance(parseFloat(getChannelData("ifood").finance))}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    Neemo
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {getChannelData("neemo").orders}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {formatFinance(parseFloat(getChannelData("neemo").finance))}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    D. Direto
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {getChannelData("ddireto").orders}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                    }}
                  >
                    {formatFinance(
                      parseFloat(getChannelData("ddireto").finance)
                    )}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    TOTAL
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {getTotalOrders()}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {formatFinance(parseFloat(getTotalFinance()))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ borderStyle: "dashed", borderWidth: "1px", my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}
          >
            MÉTODOS DE PAGAMENTO
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Método
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Sistema
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Informado
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Diferença
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.payment_methods?.map((paymentMethod, index) => (
                  <TableRow key={`payment-method-${index}`}>
                    <TableCell
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                      }}
                    >
                      {paymentMethod.payment_method}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                      }}
                    >
                      {formatFinance(
                        parseFloat(paymentMethod.calculated_value_cents / 100)
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                      }}
                    >
                      {formatFinance(
                        parseFloat(paymentMethod.provided_value_cents / 100)
                      )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                        fontWeight:
                          paymentMethod.difference_cents !== 0
                            ? "bold"
                            : "normal",
                      }}
                    >
                      {formatFinance(
                        parseFloat(paymentMethod.difference_cents / 100)
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell
                    colSpan={1}
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    TOTAL
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {formatFinance(
                      parseFloat(
                        reportData.payment_methods?.reduce(
                          (sum, item) => sum + item.calculated_value_cents,
                          0
                        ) / 100 || 0
                      )
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {formatFinance(
                      parseFloat(
                        reportData.payment_methods?.reduce(
                          (sum, item) => sum + item.provided_value_cents,
                          0
                        ) / 100 || 0
                      )
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {formatFinance(
                      parseFloat(
                        reportData.payment_methods?.reduce(
                          (sum, item) => sum + item.difference_cents,
                          0
                        ) / 100 || 0
                      )
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ borderStyle: "dashed", borderWidth: "1px", my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", textAlign: "center", mb: 1 }}
          >
            OPERAÇÕES DE CAIXA
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Tipo
                  </TableCell>
                  <TableCell
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Hora
                  </TableCell>
                  <TableCell
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Descrição
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ border: "none", py: 0.5, px: 0, fontSize: "0.75rem" }}
                  >
                    Valor
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cashier.transactions?.map((operation, index) => (
                  <TableRow key={index}>
                    <TableCell
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                      }}
                    >
                      {cashierTransactionTypeFilter(operation.transaction_type)}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                      }}
                    >
                      {timeMask(operation.created_at)}
                    </TableCell>
                    <TableCell
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                      }}
                    >
                      {operation.description}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        border: "none",
                        py: 0.5,
                        px: 0,
                        fontSize: "0.75rem",
                      }}
                    >
                      {formatFinance(parseFloat(operation.amount_cents / 100))}
                    </TableCell>
                  </TableRow>
                ))}

                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    BALANÇO
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      border: "none",
                      borderTop: "1px dashed #000",
                      py: 0.5,
                      px: 0,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {formatFinance(
                      parseFloat(cashier.total_additions / 100 || 0) -
                        parseFloat(cashier.total_withdrawals / 100 || 0)
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ borderStyle: "dashed", borderWidth: "1px", my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              PEDIDOS CANCELADOS ({getCancelledOrdersTotal()})
            </Typography>
            <IconButton
              size="small"
              onClick={() => setExpandedCancelled(!expandedCancelled)}
              sx={{ p: 0 }}
            >
              {expandedCancelled ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={expandedCancelled}>
            {cashier.cancelled_orders && cashier.cancelled_orders.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          border: "none",
                          py: 0.5,
                          px: 0,
                          fontSize: "0.75rem",
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "none",
                          py: 0.5,
                          px: 0,
                          fontSize: "0.75rem",
                        }}
                      >
                        Canal
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          border: "none",
                          py: 0.5,
                          px: 0,
                          fontSize: "0.75rem",
                        }}
                      >
                        Valor
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashier.cancelled_orders.map((order, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell
                            sx={{
                              border: "none",
                              py: 0.5,
                              px: 0,
                              fontSize: "0.75rem",
                            }}
                          >
                            {order.order_index}
                          </TableCell>
                          <TableCell
                            sx={{
                              border: "none",
                              py: 0.5,
                              px: 0,
                              fontSize: "0.75rem",
                            }}
                          >
                            {order.channel}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              border: "none",
                              py: 0.5,
                              px: 0,
                              fontSize: "0.75rem",
                            }}
                          >
                            {formatFinance(order.total)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            sx={{
                              border: "none",
                              py: 0,
                              px: 0,
                              pb: 1,
                              fontSize: "0.7rem",
                              fontStyle: "italic",
                            }}
                          >
                            Motivo: {order.cancel_reason}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}

                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{
                          border: "none",
                          borderTop: "1px dashed #000",
                          py: 0.5,
                          px: 0,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        TOTAL CANCELAMENTOS
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          border: "none",
                          borderTop: "1px dashed #000",
                          py: 0.5,
                          px: 0,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        {formatFinance(
                          parseFloatToFixed(getCancelledOrdersFinance(), 2)
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" sx={{ textAlign: "center", py: 1 }}>
                Não há pedidos cancelados neste período.
              </Typography>
            )}
          </Collapse>

          {!expandedCancelled && (
            <ThermalTicketRow
              label="Total Cancelamentos:"
              value={formatFinance(getCancelledOrdersFinance())}
            />
          )}
        </Box>

        <Divider sx={{ borderStyle: "dashed", borderWidth: "1px", my: 2 }} />

        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography variant="body2">
            Operador: {cashier.first_name} {cashier.last_name}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {dateTimeMask(startDate)} - {dateTimeMask(endDate)}
          </Typography>

          <Box
            sx={{
              my: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <QRCode
              value={qrCodeUrl}
              size={120}
              style={{
                height: "auto",
                maxWidth: "100%",
                width: "100px",
                margin: "0 auto",
              }}
              viewBox={`0 0 256 256`}
              bgColor="#FFFFFF"
              fgColor="#000000"
              level="L"
            />
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1, fontSize: "0.6rem" }}
            >
              SCAN PARA DETALHES COMPLETOS
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mt: 2 }}>
            *** FECHAMENTO DE CAIXA ***
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CashierClosingTicket;
